import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterValueGroup } from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import type { OwnerGroupData } from '@/hooks/objectives/useObjectiveOwners';
import type { ObjectiveStatusOrDraft } from '@/utils/objectiveStatus';

import {
    asObjectiveStatusOrDraft,
    getObjectiveStatusOrDraft,
    OBJECTIVE_STATUS_FILTER_VALUES,
    OBJECTIVE_STATUS_LABEL_KEYS,
    pickObjectiveStatuses,
} from '@/utils/objectiveStatus';

interface StatusSelection {
    /** The states picked, as the caller keeps them; a value that names no state is ignored. */
    selectedValues: string[];
    /** The states the groups are narrowed to, when the caller sets the picked ones aside. */
    appliedValues?: string[];
    onChange: (values: ObjectiveStatusOrDraft[]) => void;
}

/**
 * Narrows the owner groups to the objective states the reader picked, and describes the menu
 * column those states are picked from. The caller keeps the selection (the objectives page keeps it
 * in the address).
 *
 * The whole list is already in the browser -- the server sends the period's objectives in one go
 * and only filters them by pillar and window -- so the states, which are derived and never stored,
 * are matched here. Nothing picked means every state, never none.
 */
export const useObjectiveStatusFilter = (
    groups: OwnerGroupData[],
    { selectedValues, appliedValues = selectedValues, onChange }: StatusSelection,
) => {
    const { t } = useTranslation();
    const selectedStatuses = useMemo(() => pickObjectiveStatuses(selectedValues), [selectedValues]);
    const appliedStatuses = useMemo(() => pickObjectiveStatuses(appliedValues), [appliedValues]);
    // The menu's "Clear all" toggles every picked state in the same turn, before the caller's
    // selection comes back through a render: each toggle builds on the one before it, or only the
    // last would hold and a click would take off one state at a time.
    const latestSelection = useRef(selectedStatuses);
    latestSelection.current = selectedStatuses;

    const toggleStatus = (value: string) => {
        const status = asObjectiveStatusOrDraft(value);

        if (!status) {
            return;
        }

        const current = latestSelection.current;
        const next = current.includes(status)
            ? current.filter((selected) => selected !== status)
            : [...current, status];
        latestSelection.current = next;
        onChange(next);
    };

    const menuGroup: FilterValueGroup = {
        label: t('objectives.filters.status'),
        options: OBJECTIVE_STATUS_FILTER_VALUES.map((status) => ({
            label: t(OBJECTIVE_STATUS_LABEL_KEYS[status]),
            value: status,
        })),
        selectedValues: selectedStatuses,
        onToggle: toggleStatus,
    };

    const filteredGroups = useMemo(() => {
        if (appliedStatuses.length === 0) {
            return groups;
        }

        return (
            groups
                .map((group) => ({
                    ...group,
                    objectives: group.objectives.filter((objective) =>
                        appliedStatuses.includes(getObjectiveStatusOrDraft(objective)),
                    ),
                }))
                // An owner whose objectives are all in other states leaves the list with them.
                .filter((group) => group.objectives.length > 0)
        );
    }, [groups, appliedStatuses]);

    return { menuGroup, groups: filteredGroups };
};
